import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Pencil, Eye, Search, FileText, SlidersHorizontal, Users } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { PageHeader } from "@/components/shared/PageHeader";
import { TableCard } from "@/components/shared/TableCard";
import { TablePagination } from "@/components/shared/TablePagination";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useInvoiceCreationBlocked } from "@/hooks/useInvoiceCreationBlocked";
import { usePaginationFromUrl } from "@/hooks/usePaginationFromUrl";
import { formatDisplayDate } from "@/lib/dateFormat";
import { clientKeys } from "@/lib/queryKeys";
import { ClientMobileCard } from "@/pages/clients/ClientMobileCard";
import { listClients, type Client } from "@/services/clientService";

const SEARCH_DEBOUNCE_MS = 300;

export function ClientsPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { blocked: invoicingBlocked } = useInvoiceCreationBlocked();
  const [searchParams, setSearchParams] = useSearchParams();
  const pagination = usePaginationFromUrl();

  const filterSearch = searchParams.get("search") ?? "";
  const filterDocumentType = searchParams.get("documentType") ?? "";
  const filterDefaultCurrency = searchParams.get("defaultCurrency") ?? "";

  const [searchInput, setSearchInput] = useState(filterSearch);
  useEffect(() => {
    setSearchInput(filterSearch);
  }, [filterSearch]);

  const setFilter = useCallback(
    (
      key: "search" | "documentType" | "defaultCurrency",
      value: string
    ) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          next.set("page", "1");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const clearFilters = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("search");
        next.delete("documentType");
        next.delete("defaultCurrency");
        next.set("page", "1");
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filterSearch.trim() || filterDocumentType || filterDefaultCurrency
      ),
    [filterSearch, filterDocumentType, filterDefaultCurrency]
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filterDocumentType) n++;
    if (filterDefaultCurrency) n++;
    return n;
  }, [filterDocumentType, filterDefaultCurrency]);

  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== filterSearch) {
        setFilter("search", trimmed);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput, filterSearch, setFilter]);

  const listParams = useMemo(
    () => ({
      page: pagination.page,
      limit: pagination.limit,
      ...(filterSearch.trim() && { search: filterSearch.trim() }),
      ...(filterDocumentType && {
        documentType: filterDocumentType as "RUT" | "CI" | "EXT",
      }),
      ...(filterDefaultCurrency && {
        defaultCurrency: filterDefaultCurrency as "USD" | "UYU",
      }),
    }),
    [
      pagination.page,
      pagination.limit,
      filterSearch,
      filterDocumentType,
      filterDefaultCurrency,
    ]
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error: _queryError,
    refetch,
  } = useQuery({
    queryKey: clientKeys.list(listParams),
    queryFn: () => listClients(listParams),
  });

  useEffect(() => {
    if (data != null) {
      pagination.setTotal(data.total);
    } else if (isError) {
      pagination.setTotal(0);
    }
  }, [data, isError, pagination]);

  useEffect(() => {
    if (isError) {
      toast.error(
        t(
          "clients.list.error",
          "Unable to load clients. The API may not be available yet."
        )
      );
    }
  }, [isError, t]);

  const clients: Client[] = data?.data ?? [];
  const loading = isLoading;

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        eyebrow={t("clients.list.eyebrow", "Tu cartera de clientes")}
        title={t("clients.list.title", "Clients")}
        actions={
          <Button asChild>
            <Link to="/clients/new">
              {t("clients.list.addClient", "Add client")}
            </Link>
          </Button>
        }
      />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[200px] flex-1 sm:max-w-[260px] sm:flex-none">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            type="search"
            placeholder={t("clients.list.filters.searchPlaceholder", "Search by name")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-9"
            aria-label={t("clients.list.filters.searchPlaceholder", "Search by name")}
          />
        </div>

        <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-10 gap-2 transition-colors duration-150"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0" aria-hidden />
              <span className="truncate">
                {t("clients.list.filters.filters", "Filters")}
              </span>
              {activeFilterCount > 0 ? (
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                  {activeFilterCount}
                </Badge>
              ) : null}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[min(90vh,720px)] w-[calc(100vw-1.5rem)] max-w-lg gap-0 overflow-hidden p-0 sm:rounded-xl">
            <DialogHeader className="border-b border-border px-4 py-4 text-left">
              <DialogTitle className="text-foreground">
                {t("clients.list.filters.filtersSheetTitle", "Filter clients")}
              </DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[min(70vh,560px)] flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="clients-filter-document-type">
                  {t("clients.list.filters.documentType", "Document type")}
                </Label>
                <Select
                  value={filterDocumentType || "all"}
                  onValueChange={(v) =>
                    setFilter("documentType", v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger
                    id="clients-filter-document-type"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t(
                        "clients.list.filters.allDocumentTypes",
                        "All document types"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t(
                        "clients.list.filters.allDocumentTypes",
                        "All document types"
                      )}
                    </SelectItem>
                    <SelectItem value="RUT">RUT</SelectItem>
                    <SelectItem value="CI">CI</SelectItem>
                    <SelectItem value="EXT">EXT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="clients-filter-default-currency">
                  {t("clients.list.filters.defaultCurrency", "Default currency")}
                </Label>
                <Select
                  value={filterDefaultCurrency || "all"}
                  onValueChange={(v) =>
                    setFilter("defaultCurrency", v === "all" ? "" : v)
                  }
                >
                  <SelectTrigger
                    id="clients-filter-default-currency"
                    className="w-full"
                  >
                    <SelectValue
                      placeholder={t(
                        "clients.list.filters.allCurrencies",
                        "All currencies"
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">
                      {t(
                        "clients.list.filters.allCurrencies",
                        "All currencies"
                      )}
                    </SelectItem>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="UYU">UYU</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div
              className={cn(
                "flex flex-col gap-2 border-t border-border bg-muted/30 px-4 py-3 sm:flex-row sm:items-center",
                activeFilterCount > 0 ? "sm:justify-between" : "sm:justify-end"
              )}
            >
              {activeFilterCount > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="w-full transition-colors duration-150 sm:w-auto"
                  onClick={clearFilters}
                >
                  {t("clients.list.filters.clearFilters", "Clear filters")}
                </Button>
              ) : null}
              <Button
                type="button"
                className="w-full transition-colors duration-150 sm:w-auto"
                onClick={() => setFiltersOpen(false)}
              >
                {t("clients.list.filters.filtersDone", "Done")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {hasActiveFilters ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-muted-foreground transition-colors duration-150"
            onClick={clearFilters}
          >
            {t("clients.list.filters.clearFilters", "Clear filters")}
          </Button>
        ) : null}
      </div>
      <TableCard>
        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center py-12">
            <LoadingLogo size="lg" />
          </div>
        ) : clients.length === 0 ? (
          <EmptyState
            icon={<Users />}
            title={t("clients.list.empty", "No clients yet.")}
            message={t(
              "clients.list.emptyHint",
              "Add a client to reuse their details when creating invoices."
            )}
            action={
              <Button asChild>
                <Link to="/clients/new">
                  {t("clients.list.addClient", "Add client")}
                </Link>
              </Button>
            }
          />
        ) : (
          <>
            <div className="space-y-3 py-2 md:hidden">
              {clients.map((client) => (
                <ClientMobileCard
                  key={client.id}
                  client={client}
                  invoicingBlocked={invoicingBlocked}
                />
              ))}
            </div>
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("clients.list.legalName", "Legal name")}
                  </TableHead>
                  <TableHead>
                    {t("clients.list.fantasyName", "Fantasy name")}
                  </TableHead>
                  <TableHead>{t("clients.list.taxIdOrDocument", "RUT / EIN")}</TableHead>
                  <TableHead>
                    {t("clients.list.dateAdded", "Date added")}
                  </TableHead>
                  <TableHead className="w-[60px]" aria-hidden />
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => {
                  const actions: TableAction[] = [
                    {
                      label: t("clients.list.invoice", "Create invoice"),
                      icon: FileText,
                      onClick: () =>
                        navigate("/invoices/new", { state: { client } }),
                      disabled: invoicingBlocked,
                    },
                    {
                      label: t("clients.list.edit", "Edit"),
                      icon: Pencil,
                      onClick: () =>
                        navigate(`/clients/${client.id}/edit`),
                    },
                    {
                      label: t("clients.list.viewDetails", "View details"),
                      icon: Eye,
                      onClick: () => navigate(`/clients/${client.id}`),
                    },
                  ];
                  const dateAdded = client.createdAt
                    ? formatDisplayDate(client.createdAt, {
                        locale: i18n.language,
                      })
                    : "—";
                  return (
                    <TableRow
                      key={client.id}
                      onRowClick={() => navigate(`/clients/${client.id}`)}
                    >
                      <TableCell className="font-medium">
                        {client.legalName}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {client.displayName ?? "—"}
                      </TableCell>
                      <TableCell>
                        {client.documentType === "EXT"
                          ? (client.docRecepExt?.trim() ?? "—")
                          : (client.rut ?? "—")}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {dateAdded}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <TableActionsMenu actions={actions} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
            <TablePagination
              pagination={pagination}
              rowsPerPageLabel={t(
                "clients.list.rowsPerPage",
                "Rows per page"
              )}
              pageOfLabel={t(
                "clients.list.pageOf",
                "{{from}}-{{to}} of {{total}}",
                {
                  from:
                    (pagination.page - 1) * pagination.limit + 1,
                  to: Math.min(
                    pagination.page * pagination.limit,
                    pagination.total
                  ),
                  total: pagination.total,
                }
              )}
              onRefresh={() => void refetch()}
              isRefreshing={isFetching}
            />
          </>
        )}
      </TableCard>
    </div>
  );
}
